import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const resultClass = (i) => {
  if (i.status === "completed" && i.result === "pass") return "bg-[#D1FAE5] text-[#065F46] border-l-2 border-[#059669]";
  if (i.status === "completed" && i.result === "fail") return "bg-[#FEE2E2] text-[#B91C1C] border-l-2 border-[#DC2626]";
  if (i.status === "in_progress") return "bg-[#FEF3C7] text-[#92400E] border-l-2 border-[#D97706]";
  return "bg-[#E0F2FE] text-[#075985] border-l-2 border-[#0284C7]";
};

export default function CalendarView({ inspections }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });

  const { year, month, weeks } = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    // Monday=0, ... Sunday=6
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return { year: y, month: m, weeks };
  }, [cursor]);

  const byDate = useMemo(() => {
    const map = {};
    for (const i of inspections) {
      const key = (i.scheduled_date || "").slice(0, 10);
      if (!key) continue;
      (map[key] ||= []).push(i);
    }
    return map;
  }, [inspections]);

  const monthInspections = useMemo(() => inspections.filter((i) => {
    const k = (i.scheduled_date || "").slice(0, 10);
    return k.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`);
  }), [inspections, year, month]);
  const prev = () => setCursor(new Date(year, month - 1, 1));
  const next = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => { const d = new Date(); d.setDate(1); setCursor(d); };

  return (
    <div className="bg-white border border-gray-200" data-testid="calendar-view">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight" data-testid="calendar-month-label">{MONTHS[month]} {year}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{monthInspections.length} inspection{monthInspections.length === 1 ? "" : "s"} this month</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={goToday} data-testid="calendar-today" className="text-xs uppercase tracking-wider font-semibold px-3 py-2 border border-gray-300 hover:bg-gray-50">Today</button>
          <button onClick={prev} data-testid="calendar-prev" className="w-9 h-9 border border-gray-300 hover:bg-gray-50 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={next} data-testid="calendar-next" className="w-9 h-9 border border-gray-300 hover:bg-gray-50 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-gray-600 text-center">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {(() => {
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          const today = fmt(new Date());
          return weeks.flat().map((cell, idx) => {
          if (!cell) {
            return <div key={`cal-empty-${idx}`} className="min-h-[120px] border-r border-b border-gray-100 bg-gray-50/40" />;
          }
          const key = fmt(cell);
          const list = byDate[key] || [];
          const isToday = key === today;
          return (
            <div key={`cal-${key}`} className="min-h-[120px] border-r border-b border-gray-100 p-2 flex flex-col gap-1" data-testid={`cal-cell-${key}`}>
              <div className={`text-xs font-mono ${isToday ? "inline-flex items-center justify-center w-6 h-6 bg-[#01ADEF] text-white font-bold" : "text-gray-500"}`}>{cell.getDate()}</div>
              <div className="flex flex-col gap-1">
                {list.slice(0, 3).map((i) => (
                  <Link
                    key={i.id}
                    to={`/inspections/${i.id}`}
                    data-testid={`cal-item-${i.id}`}
                    className={`text-xs px-2 py-1 truncate hover:opacity-80 ${resultClass(i)}`}
                    title={`${i.asset_name || "Asset"} — ${i.inspection_type}`}
                  >
                    <span className="font-semibold">{i.asset_name || "Asset"}</span>
                    <span className="opacity-70"> · {i.inspection_type?.replace("_", " ")}</span>
                  </Link>
                ))}
                {list.length > 3 && (
                  <div className="text-xs text-gray-500 px-2">+{list.length - 3} more</div>
                )}
              </div>
            </div>
          );
        });
        })()}
      </div>
    </div>
  );
}
